/* eslint-disable max-lines */
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Card,
    CardContent,
    Icon,
    IconButton,
    LinearProgress,
    Tooltip,
    Typography,
} from '@mui/material';
import { DataGridPro, GridColDef, GridRowParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { DateRange } from '@mui/x-date-pickers-pro';
import { format } from 'date-fns';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { If } from 'app/components';
import { RequestStatus } from 'app/components/request-status';
import { RouterLink } from 'app/components/router-link';
import { SeamRequestLink } from 'app/components/seam-request-link';
import { SearchBoxOptionValue } from 'app/components/search-box/search-box-types';
import { CrisRequestType, GeneralConstants, SeamStatus, SeamTransitions } from 'app/constants';
import { TimingConstants } from 'app/constants/timing-constants';
import { Store } from 'app/model/cris/store';
import { SeamRequest } from 'app/model/seam-request';
import { User } from 'app/model/user';
import { seamRequestService } from 'app/services/seam-request.service';
import { DataGridFormatters } from 'app/utils/data-grid-formatters';
import { Formatters } from 'app/utils/formatters';
import { Strings } from 'app/utils/strings';

import {
    downloadRequests,
    findRequests,
    selectResults,
    selectSortModel,
    setAssignedTo,
    setStatus,
    setTerm,
    setTuner,
    setSortModel,
    setCreatedAfter,
    setCreatedBefore,
    setPreProdDeployAfter,
    setPreProdDeployBefore,
    setProdDeployAfter,
    setProdDeployBefore,
} from './explore.slice';
import { ExplorerSearchBox } from './explorer-search-box';

const columns: GridColDef[] = [
    {
        field: 'id',
        headerName: 'Request Id',
        width: 120,
        renderCell: (params) => {
            const seamReq = params.row as SeamRequest;

            return <SeamRequestLink id={seamReq.id} />;
        },
    },
    {
        field: 'correction',
        headerName: 'Action',
        width: 120,
        disableColumnMenu: true,
        valueGetter: (params) => (params.value ? 'Unmap' : 'Map'), // todo translation
    },
    {
        field: 'store_id',
        headerName: 'Store Id',
        width: 100,
        valueGetter: (params) => {
            const store = (params.row as SeamRequest).store;

            return store.id;
        },
    },
    {
        field: 'store',
        headerName: 'Store',
        width: 300,
        renderCell: (params) => {
            const store = params.value as Store;

            return (
                <div>
                    <RouterLink to={`/cris/stores/${store.id}`} target='_blank'>
                        {store.name}
                    </RouterLink>

                    {' ('}
                    {store.brand ? (
                        <RouterLink to={`/cris/brands/${store.brand.id}`} target='_blank'>
                            {store.brand.name}
                        </RouterLink>
                    ) : (
                        '[none]'
                    )}
                    {')'}
                </div>
            );
        },
    },

    {
        field: 'currentPlace',
        headerName: 'Status',
        width: 200,
        renderCell: (params) => <RequestStatus status={params.value as SeamStatus} />,
    },
    {
        field: 'patternTypes',
        headerName: 'Pattern Types',
        width: 200,
        valueGetter: (params) => {
            return (params.value as CrisRequestType[]).map((p) => Strings.patternType(p)).join(', ');
        },
    },
    {
        field: 'owner',
        headerName: 'Assigned To',
        width: 200,
        valueGetter: (params) => {
            const user = params.value as User | undefined;

            return user ? Formatters.userDisplayName(user) : '[unassigned]';
        },
    },
    {
        field: 'tuner',
        headerName: 'Pattern Creator',
        width: 200,
        valueGetter: (params) => {
            const user = params.value as User | undefined;

            return user ? Formatters.userDisplayName(user) : '[unassigned]';
        },
    },
    {
        field: 'manager',
        headerName: 'Manager',
        width: 200,
        valueGetter: (params) => {
            const user = params.value as User | undefined;

            return user ? Formatters.userDisplayName(user) : '[unassigned]';
        },
    },
    {
        field: 'publishDates.preProd',
        headerName: 'PreProd Publish',
        width: 200,
        valueFormatter: DataGridFormatters.dateTimeFormatter,
        valueGetter: (params) => {
            const dates = (params.row as SeamRequest).publishDates;

            return dates.preProd;
        },
    },
    {
        field: 'publishDates.prod',
        headerName: 'Prod Publish',
        width: 200,
        valueFormatter: DataGridFormatters.dateTimeFormatter,
        valueGetter: (params) => {
            const dates = (params.row as SeamRequest).publishDates;

            return dates.prod;
        },
    },
];

export const Search: React.FunctionComponent = () => {
    const dispatch = useDispatch();

    const results = useSelector(selectResults);
    const sortModel = useSelector(selectSortModel);

    const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
    const [processed, setProcessed] = useState(0);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);

    const batchUpdater = (ids: GridRowSelectionModel): void => {
        if (0 === ids.length) {
            // we are done
            setShowSuccessMessage(true);
            setTimeout(() => {
                setShowSuccessMessage(false);
            }, TimingConstants.SUCCESS_MESSEGE_DELAY);

            return;
        }

        // we need to run 1 at a time for 2 reasons:
        // 1. do not overload the server
        // 2. more imporantly, automated actions are linked to transitions that depend on other requests
        const next = String(ids.pop());

        seamRequestService.transition({ id: next }, SeamTransitions.PROD_QA_APPROVED).subscribe(() => {
            setProcessed((prev) => prev - 1);
            batchUpdater(ids);
        });
    };

    const completionProcess = parseFloat(
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        (((selectionModel.length - processed) / selectionModel.length) * 100).toFixed(1),
    );

    return (
        <React.Fragment>
            <Typography variant='h4'>Request Search</Typography>
            <Typography variant='body1' className='mb-2'>
                Search for CRIS/SEAM requests within the system. Search field accepts brand or store name.
            </Typography>
            <form
                onSubmit={(evt) => {
                    evt.preventDefault();
                }}
            >
                <Box display='flex' mb={1}>
                    <ExplorerSearchBox
                        onSearch={(values) => {
                            dispatch(
                                setTerm(
                                    values
                                        .filter((val) => 'term' === val.optionName)
                                        .map((val) => val.optionValue as string)
                                        .join(' '),
                                ),
                            );

                            const newStatus = values.find((val) => 'status' === val.optionName)?.optionValue;
                            if (newStatus) {
                                dispatch(setStatus((newStatus as SearchBoxOptionValue).value as SeamStatus));
                            } else {
                                dispatch(setStatus(''));
                            }

                            const newTuner = values.find((val) => 'tuner' === val.optionName)?.optionValue;
                            if (newTuner) {
                                dispatch(setTuner((newTuner as SearchBoxOptionValue).value as User));
                            } else {
                                dispatch(setTuner(undefined));
                            }

                            const newAssignedTo = values.find((val) => 'assignedTo' === val.optionName)?.optionValue;
                            if (newAssignedTo) {
                                dispatch(setAssignedTo((newAssignedTo as SearchBoxOptionValue).value as User));
                            } else {
                                dispatch(setAssignedTo(undefined));
                            }

                            const dateCreated = values.find((val) => 'dateCreated' === val.optionName)?.optionValue as
                                | DateRange<Date>
                                | undefined;
                            if (dateCreated?.[0]) {
                                dispatch(setCreatedAfter(format(dateCreated[0], 'yyyy-MM-dd')));
                            } else {
                                dispatch(setCreatedAfter(undefined));
                            }
                            if (dateCreated?.[1]) {
                                dispatch(setCreatedBefore(format(dateCreated[1], 'yyyy-MM-dd')));
                            } else {
                                dispatch(setCreatedBefore(undefined));
                            }

                            const preProdDeploy = values.find((val) => 'preProdDeploy' === val.optionName)
                                ?.optionValue as DateRange<Date> | undefined;
                            if (preProdDeploy?.[0]) {
                                dispatch(setPreProdDeployAfter(format(preProdDeploy[0], 'yyyy-MM-dd')));
                            } else {
                                dispatch(setPreProdDeployAfter(undefined));
                            }
                            if (preProdDeploy?.[1]) {
                                dispatch(setPreProdDeployBefore(format(preProdDeploy[1], 'yyyy-MM-dd')));
                            } else {
                                dispatch(setPreProdDeployBefore(undefined));
                            }

                            const prodDeploy = values.find((val) => 'prodDeploy' === val.optionName)?.optionValue as
                                | DateRange<Date>
                                | undefined;
                            if (prodDeploy?.[0]) {
                                dispatch(setProdDeployAfter(format(prodDeploy[0], 'yyyy-MM-dd')));
                            } else {
                                dispatch(setProdDeployAfter(undefined));
                            }
                            if (prodDeploy?.[1]) {
                                dispatch(setProdDeployBefore(format(prodDeploy[1], 'yyyy-MM-dd')));
                            } else {
                                dispatch(setProdDeployBefore(undefined));
                            }

                            dispatch(findRequests());
                        }}
                    />
                    <Tooltip title='Download'>
                        <IconButton
                            color='default'
                            onClick={() => dispatch(downloadRequests())}
                            size='small'
                            sx={{ minWidth: '46px', ml: 1 }}
                        >
                            <Icon>file_download</Icon>
                        </IconButton>
                    </Tooltip>
                </Box>
            </form>
            <If condition={0 < selectionModel.length}>
                <Card variant='outlined' className='my-1'>
                    <CardContent>
                        <Typography variant='body2' gutterBottom>
                            Batch Actions
                        </Typography>
                        <Button
                            variant='outlined'
                            color='primary'
                            type='button'
                            onClick={() => {
                                setProcessed(selectionModel.length);
                                batchUpdater([...selectionModel]);
                            }}
                            disabled={0 < processed}
                        >
                            Approve Prod QA
                        </Button>
                        <If condition={0 < processed}>
                            <Typography variant='body2' gutterBottom>
                                Remaining {processed} of {selectionModel.length} {completionProcess}%
                            </Typography>
                            <LinearProgress variant='determinate' value={completionProcess} />
                        </If>
                        <Box marginTop={1}>
                            <If condition={showSuccessMessage}>
                                <Alert severity='success'>
                                    <AlertTitle>Success</AlertTitle>
                                    Requests Successfully Transitioned.
                                </Alert>
                            </If>
                        </Box>
                    </CardContent>
                </Card>
            </If>
            <Box sx={{ width: '100%', display: 'grid', height: '100%' }}>
                <DataGridPro
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                manager: false,
                            },
                        },
                    }}
                    rows={results}
                    columns={columns}
                    checkboxSelection={true}
                    pagination
                    paginationMode='client'
                    disableRowSelectionOnClick
                    autoHeight
                    density='compact'
                    pageSizeOptions={GeneralConstants.PAGE_SIZE_OPTIONS}
                    onRowSelectionModelChange={setSelectionModel}
                    isRowSelectable={(params: GridRowParams<SeamRequest>) =>
                        params.row.currentPlace === SeamStatus.PROD_QA
                    }
                    sortModel={sortModel}
                    onSortModelChange={(newSortModel) => dispatch(setSortModel(newSortModel))}
                />
            </Box>
        </React.Fragment>
    );
};
